/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { ActionClickedResponse } from 'src/app/models/ActionClickedResponse';
import { CellChangedResponse } from 'src/app/models/CellChangedResponse';
import { GenericTableConfiguration } from 'src/app/models/GenericTableConfiguration';
import { EditMethod } from 'src/app/types/misc/EditMethod';
import { MessageCenterService } from '../../../services/message-center.service';
import { FormComponent } from './form/form.component';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import { GenericTableComponent } from '../../misc/generic-table/generic-table.component';
import { ButtonModule } from 'primeng/button';
import { SidebarComponent } from './sidebar/sidebar.component';

@Component({
  selector: 'app-crud',
  templateUrl: './crud.component.html',
  styleUrls: ['./crud.component.scss'],
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    GenericTableComponent,
    ButtonModule,
    FormComponent,
    SidebarComponent
  ]
})
export class CrudComponent implements OnInit, OnChanges {
  private translate = inject(TranslateService);
  private messageCenterService = inject(MessageCenterService);

  /**
   * Indicates whether the table is visible.
   */
  @Input() tableVisible = true;

  /**
   * The configuration for the generic table.
   */
  @Input() config!: GenericTableConfiguration;

  /**
   * The list of objects to be displayed in the table.
   */
  @Input() objectsList: any[] = [];

  /**
   * Indicates whether the sidebar is visible.
   */
  @Input() sidebarVisible = false;

  /**
   * Indicates whether the separate page is visible.
   */
  @Input() separatePageVisible = false;

  /**
   * The method used for editing (e.g., 'sidebar' or 'page').
   */
  @Input() editMethod!: EditMethod;

  /**
   * The method used for creating (e.g., 'sidebar' or 'page').
   */
  @Input() createMethod!: EditMethod;

  /**
   * The object being edited, or null if none.
   */
  @Input() editObject?: any | null;

  /**
   * The total number of records available.
   */
  @Input() totalRecords!: number;

  /**
   * Event emitted when an object is changed.
   */
  @Output() objectChanged = new EventEmitter<any>();

  /**
   * Event emitted when a cell is changed.
   */
  @Output() cellChanged = new EventEmitter<CellChangedResponse>();

  /**
   * Event emitted when the selection is changed.
   */
  @Output() selectionChanged = new EventEmitter<any[]>();

  /**
   * Event emitted when a row action is clicked.
   */
  @Output() rowActionClicked = new EventEmitter<ActionClickedResponse>();

  /**
   * Event emitted when the sidebar visibility is changed.
   */
  @Output() sidebarVisibleChange = new EventEmitter<boolean>();

  /**
   * Event emitted when the separate page visibility is changed.
   */
  @Output() separatePageVisibleChange = new EventEmitter<boolean>();

  /**
   * Event emitted when the table visibility is changed.
   */
  @Output() tableVisibleChange = new EventEmitter<boolean>();

  /**
   * Event emitted when a lazy load table event is fired.
   */
  @Output() lazyLoadTableEventFired = new EventEmitter<TableLazyLoadEvent>();

  /**
   * Reference to the form component.
   * Used to access the form's properties and methods.
   */
  @ViewChild('form') formComponent?: FormComponent;

  /**
   * Template reference for logging.
   * Used to render the logging template.
   */
  @ContentChild('loggingTemplate', { static: true })
  loggingTemplate!: TemplateRef<unknown>;

  editSubmitDisabled!: boolean;

  createSubmitDisabled!: boolean;

  ngOnInit() {
    this.editSubmitDisabled = this.getEditSubmitDisabled();
    this.createSubmitDisabled = this.getCreateSubmitDisabled();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editObject']) {
      this.editSubmitDisabled = this.getEditSubmitDisabled();
      this.createSubmitDisabled = this.getCreateSubmitDisabled();
    }
  }

  /**
   * Handles changes to the form.
   * Updates the state of the submit buttons based on the form's validity.
   *
   * @returns {void}
   */
  formChanged(): void {
    this.editSubmitDisabled = this.getEditSubmitDisabled();
    this.createSubmitDisabled = this.getCreateSubmitDisabled();
  }

  /**
   * Emits the objectChanged event with the provided object.
   *
   * @param {any} object - The object that has changed.
   * @returns {void}
   */
  onObjectChanged(object: any): void {
    this.objectChanged.emit(object);
  }

  /**
   * Emits the cellChanged event with the provided cell change response.
   *
   * @param {CellChangedResponse} object - The response object containing the cell change details.
   * @returns {void}
   */
  onCellChanged(object: CellChangedResponse): void {
    this.cellChanged.emit(object);
  }

  /**
   * Emits the selectionChanged event with the provided list of selected items.
   *
   * @param {any} list - The list of selected items.
   * @returns {void}
   */
  onSelectionChanged(list: any): void {
    this.selectionChanged.emit(list);
  }

  /**
   * Handles the action click event.
   * If the action requires confirmation, it prompts the user to confirm the action.
   * If the user confirms, it emits the rowActionClicked event with the response.
   * If the user cancels, it shows an informational toast message.
   * If the action does not require confirmation, it directly emits the rowActionClicked event with the response.
   *
   * @param {ActionClickedResponse} response - The response object containing the action details.
   * @returns {Promise<void>} - A promise that resolves when the action click is handled.
   */
  async onActionClicked(response: ActionClickedResponse): Promise<void> {
    if (response.action.confirmRequired) {
      await new Promise<boolean>((resolve) => {
        this.messageCenterService.confirm(
          this.translate.instant('general.confirmDelete.header'),
          this.translate.instant('general.confirmDelete.message'),
          () => {
            this.rowActionClicked.emit(response);
          },
          () => {
            this.messageCenterService.showToast(
              this.translate.instant(
                `${this.config.translationKey}.actions.toasts.${response.action.identifier}.info.summary`
              ),
              this.translate.instant(
                `${this.config.translationKey}.actions.toasts.${response.action.identifier}.info.detail`
              ),
              'info'
            );

            resolve(false);
          }
        );
      });
    } else {
      this.rowActionClicked.emit(response);
    }
  }

  /**
   * Handles the visibility change of the sidebar.
   * If the form is dirty and the sidebar is being hidden, it prompts the user to confirm unsaved changes.
   * If the user confirms, it hides the sidebar and clears the edit object.
   * Otherwise, it simply updates the visibility state of the sidebar.
   *
   * @param {boolean} visible - The new visibility state of the sidebar.
   * @returns {Promise<void>} - A promise that resolves when the visibility change is handled.
   */
  async onSidebarVisibleChange(visible: boolean) {
    if (
      this.editObject &&
      !visible &&
      this.formComponent &&
      this.formComponent &&
      this.formComponent.form &&
      this.formComponent.form.dirty
    ) {
      await new Promise<boolean>((resolve) => {
        this.messageCenterService.confirm(
          this.translate.instant('general.confirmUnsavedChanges.header'),
          this.translate.instant('general.confirmUnsavedChanges.message'),
          () => {
            this.editObject = null;
            this.sidebarVisible = visible;
            this.sidebarVisibleChange.emit(this.sidebarVisible);
          },
          () => {
            resolve(false);
          }
        );
      });
    } else {
      this.sidebarVisible = visible;
      this.sidebarVisibleChange.emit(this.sidebarVisible);
    }
  }

  /**
   * Handles the visibility change of the separate page.
   * If the form is dirty and the separate page is being hidden, it prompts the user to confirm unsaved changes.
   * If the user confirms, it hides the separate page and shows the table.
   * Otherwise, it simply updates the visibility state of the separate page and table.
   *
   * @param {boolean} visible - The new visibility state of the separate page.
   * @returns {Promise<void>} - A promise that resolves when the visibility change is handled.
   */
  async onSeparatePageVisibleChange(visible: boolean): Promise<void> {
    // Check if the form is dirty and the separate page is being hidden
    if (
      this.editObject &&
      !visible &&
      this.formComponent &&
      this.formComponent.form &&
      this.formComponent.form.dirty
    ) {
      // Prompt the user to confirm unsaved changes
      await new Promise<boolean>((resolve) => {
        this.messageCenterService.confirm(
          this.translate.instant('general.confirmUnsavedChanges.header'),
          this.translate.instant('general.confirmUnsavedChanges.message'),
          () => {
            // If the user confirms, hide the separate page and show the table
            this.editObject = null;
            this.separatePageVisible = visible;
            this.tableVisible = true;
            this.separatePageVisibleChange.emit(this.separatePageVisible);
            this.tableVisibleChange.emit(true);
          },
          () => {
            // If the user cancels, resolve the promise with false
            resolve(false);
          }
        );
      });
    } else {
      // Simply update the visibility state of the separate page and table
      this.separatePageVisible = visible;
      this.separatePageVisibleChange.emit(this.separatePageVisible);
      this.tableVisibleChange.emit(true);
      this.tableVisible = true;
    }
  }

  /**
   * Handles the form submission.
   * If the form is valid, it updates the editObject with the form values,
   * marks the form as untouched and pristine, and emits the objectChanged event.
   *
   * @returns {void}
   */
  onSubmit(): void {
    if (
      this.formComponent &&
      this.formComponent.form &&
      this.formComponent.form.valid
    ) {
      this.config.columns.forEach((col) => {
        if (col.formControl) {
          this.editObject[col.field] = this.formComponent?.form.get(
            col.field
          )?.value;
        }
      });
      this.formComponent.form.markAsUntouched();
      this.formComponent.form.markAsPristine();
      this.objectChanged.emit(this.editObject);
    }
  }

  /**
   * Determines whether the submit button for editing an object should be disabled.
   * The button is disabled if the form is invalid or not present.
   *
   * @returns {boolean} - True if the submit button should be disabled, false otherwise.
   */
  getEditSubmitDisabled(): boolean {
    const form = this.formComponent?.form;
    if (form) {
      return !form.valid;
    }

    return true;
  }

  /**
   * Determines whether the submit button for creating a new object should be disabled.
   * The button is disabled if the form is invalid or not present.
   *
   * @returns {boolean} - True if the submit button should be disabled, false otherwise.
   */
  getCreateSubmitDisabled(): boolean {
    const form = this.formComponent?.form;
    if (form) {
      return !form || !form.valid;
    }

    return true;
  }

  /**
   * Determines whether the component should be displayed in a sidebar.
   * Returns true if the edit or create method is set to 'sidebar', an object is being edited, and the sidebar is visible.
   *
   * @returns {boolean} - True if the component should be displayed in a sidebar, false otherwise.
   */
  displayInSidebar(): boolean {
    return (
      (this.editMethod === 'sidebar' || this.createMethod === 'sidebar') &&
      this.editObject &&
      this.sidebarVisible
    );
  }

  /**
   * Determines whether the component should be displayed in a separate page.
   * Returns true if the edit or create method is set to 'page', an object is being edited, and the separate page is visible.
   *
   * @returns {boolean} - True if the component should be displayed in a separate page, false otherwise.
   */
  displayInSeparatePage(): boolean {
    return (
      (this.editMethod === 'page' || this.createMethod === 'page') &&
      this.editObject &&
      this.separatePageVisible
    );
  }

  /**
   * Handles the lazy load event for the table.
   * Emits the lazy load event to notify other components or services.
   *
   * @param {TableLazyLoadEvent} event - The event object containing information about the lazy load request.
   * @returns {void}
   */
  onLazyLoadTableEventFired(event: TableLazyLoadEvent): void {
    this.lazyLoadTableEventFired.emit(event);
  }
}
